/**
 * WebPlacementStatusBadge Component
 *
 * Chip component displaying web placement status (Draft / Published).
 *
 * @module webplacements/components
 */

import React from 'react';
import { Chip, type ChipProps } from '@mui/material';
import type { WebPlacementStatus } from '@modules/webplacements/types/webplacements.types';

/**
 * Props for WebPlacementStatusBadge
 */
export interface WebPlacementStatusBadgeProps {
  /** Status to display */
  status: WebPlacementStatus;
  /** Chip size */
  size?: ChipProps['size'];
  /** Chip variant */
  variant?: ChipProps['variant'];
}

/**
 * Status display configuration
 */
const STATUS_CONFIG: Record<
  WebPlacementStatus,
  { label: string; color: ChipProps['color'] }
> = {
  draft: {
    label: 'Draft',
    color: 'default',
  },
  published: {
    label: 'Published',
    color: 'success',
  },
};

/**
 * WebPlacementStatusBadge Component
 */
export const WebPlacementStatusBadge: React.FC<WebPlacementStatusBadgeProps> = ({
  status,
  size = 'small',
  variant = 'outlined',
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      variant={variant}
    />
  );
};

export default WebPlacementStatusBadge;
